import { inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { AppService, SocketEventType } from '@core';
import { LocationActions } from '@locations';
import { IncidentModel } from '@models';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { ClientManagementActions, IncidentsActions, SocketEvents } from '@ngrx-store';
import { NotificationsService } from '@ui';
import { forkJoin, map, of, switchMap } from 'rxjs';
import { catchError, delay, tap } from 'rxjs/operators';

import { IncidentRulesService } from '../components/incident-rules/incident-rules.service';
import { type DeviceData } from '../models';
import { ActionPointService, DeviceAttributeService, DeviceModelService, DeviceService } from '../services';
import { DevicesActions } from './devices.actions';

@Injectable()
export class DevicesEffects {
  private actions$ = inject(Actions);
  private deviceService = inject(DeviceService);
  private deviceModelService = inject(DeviceModelService);
  private deviceAttributeService = inject(DeviceAttributeService);
  private actionPointService = inject(ActionPointService);
  private incidentRulesService = inject(IncidentRulesService);
  private appService = inject(AppService);
  private notificationService = inject(NotificationsService);
  private store = inject(Store);
  private router = inject(Router);

  constructor() {
    SocketEvents.pipe(takeUntilDestroyed())
      .subscribe(({ data, type }) => {
        switch (type) {
          case SocketEventType.DEVICE_UPDATED:
            this.store.dispatch(DevicesActions.updateDevice({ device: data }));
            break;
          case SocketEventType.INCIDENT_CREATED:
          case SocketEventType.INCIDENT_IN_PROGRESS:
            this.store.dispatch(DevicesActions.updateDevice({ device: data.device }));
            break;
          case SocketEventType.INCIDENT_RESOLVED:
            ((data?.data ?? data) as IncidentModel[]).forEach(incident => {
              this.store.dispatch(DevicesActions.updateDevice({ device: incident.device }));
            });
            break;
        }
      });
  }

  clientIdChanged$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ClientManagementActions.clientIdChanged),
      switchMap(() => [
        DevicesActions.resetDeviceEntity(),
      ]),
    );
  });

  loadAllDevicesByClient$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DevicesActions.loadAllClientDevices),
      switchMap(() =>
        this.deviceService.getAllDevicesByClientId(this.appService.currentClient).pipe(
          map(devices => DevicesActions.allClientDevicesLoaded({ devices })),
          catchError(error => of(DevicesActions.loadDevicesError({ error }))),
        ),
      ),
    ),
  );

  resetDeviceData$ = createEffect(() => this.actions$.pipe(ofType(DevicesActions.resetDeviceEntity)), {
    dispatch: false,
  });

  addNewDevice$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DevicesActions.addNewDevice),
      switchMap(action =>
        this.deviceService.addNewDevice(this.appService.currentClient, action.locationId, action.newDeviceData).pipe(
          map((device: DeviceData) => {
            this.notificationService.showSuccessMessage(
              'Device [' + device.friendlyName + '] was successfully created',
            );

            return DevicesActions.addNewDeviceSuccess({ newDevice: device });

          }),
          catchError(error => {
            this.notificationService.showErrorMessage(error.message);

            return of(DevicesActions.addNewDeviceError());
          }),
        ),
      ),
    ),
  );

  loadOneDeviceDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DevicesActions.loadDeviceDetails),
      switchMap(action =>
        this.deviceService
          .getDeviceDetailsByDeviceId(action.deviceId)
          .pipe(
            map(device => DevicesActions.loadDeviceDetailsSuccess({ deviceItem: device })),
            catchError(error => of(DevicesActions.loadDeviceDetailsFailure({ error }))),
          ),
      ),
    ),
  );

  updateDevice$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DevicesActions.updateDeviceData),
      switchMap(action => {
        return this.deviceService
          .updateDeviceData({
            data: action.data,
            deviceId: action.deviceId,
            locationId: action.locationId,
          })
          .pipe(
            map(device => {
              this.notificationService.showSuccessMessage('Device - ' + device.friendlyName + ' was updated');

              return DevicesActions.deviceDataSuccessfullyUpdated({ device, oldData: action.data });
            }),
            catchError(() => of(DevicesActions.updateDeviceDataError())),
          );
      }),
    );
  });

  updateDeviceIncidentRule$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DevicesActions.updateDeviceIncidentRule),
      switchMap(action => {
        return this.incidentRulesService
          .updateIncidentRule({
            body: action.body,
            deviceId: action.deviceId,
            incidentRuleId: action.incidentRuleId,
          })
          .pipe(
            tap(rule => {
              this.notificationService.showSuccessMessage('Incident Rule - ' + rule.code + ' was updated');
            }),
            map(rule => DevicesActions.updateDeviceIncidentRuleSuccess({ rule })),
            catchError(() => of(DevicesActions.updateDeviceDataError())),
          );
      }),
    );
  });

  loadDeviceCollectionByRoomId$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DevicesActions.loadDeviceCollectionBySpaceId),
      switchMap(action =>
        this.deviceService
          .getAllDevicesByClientId(this.appService.currentClient, {
            locationId: action.locationId,
            spaceId: action.roomId,
            withRules: action.withRules || false,
          })
          .pipe(map(devices => DevicesActions.loadDeviceCollectionBySpaceIdSuccess({ devices }))),
      ),
    ),
  );

  deviceDataSuccessfullyUpdated$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DevicesActions.deviceDataSuccessfullyUpdated),
      // TODO: get rid of delay when api is ready
      delay(5000),
      map(action => {
        if (action.oldData.status) {
          this.store.dispatch(IncidentsActions.loadAllIncidents());
        }

        return LocationActions.getLocationState({
          excludeArchived: false,
          locationId: action.device.locationId || action.device.location.id,
        });
      }),
    ),
  );

  deviceInfoCollections$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DevicesActions.loadDeviceCollections),
      switchMap(() =>
        forkJoin([
          this.deviceModelService.getModels(),
          this.deviceAttributeService.getAttributes(),
          this.actionPointService.getActionPoints(),
        ]).pipe(
          map(([models, attributes, actionPoints]) =>
            DevicesActions.loadDeviceCollectionsSuccess({ actionPoints, attributes, models }),
          ),
        ),
      ),
    ),
  );

  deleteDevice$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DevicesActions.deleteDevice),
      switchMap(action =>
        this.deviceService.deleteDevice(action.device).pipe(
          tap(async () => {
            this.notificationService.showSuccessMessage('Device removed successfully.');
            await this.router.navigate(['/devices'], { queryParamsHandling: 'merge' });
          }),
          map(() => DevicesActions.dropDevice({ deviceId: action.device.id })),
          catchError((error) => {
            this.notificationService.showErrorMessage('An unexpected error occurred. Please try again later.');

            return of(DevicesActions.deleteDeviceError({ error }));
          }),
        ),
      ),
    ),
  );
}
